import { Observable, from, of, catchError, concatMap, map, switchMap, toArray } from 'rxjs';
import type { RepositoryListItem, RepositoryListResponse } from '../../../../domain/src/lib/repository/repository.model';
import {
  extractZipInternalRelativePath,
  normalizeRepositoryExplorerPath,
  normalizeZipInternalPath,
  resolveRepositoryItemPath,
  resolveZipInternalNavigatePath,
  resolveZipInternalRowSegment,
} from './repository-assignment-path.util';

export interface RepositoryZipClientSearchCriteria {
  /** Texto de búsqueda o `__ext_only__` (solo tipo). */
  query: string;
  /** `all` | `pdf` | `image` | `text` | `zip` */
  fileType: string;
  maxResults?: number;
  maxZips?: number;
  maxDepth?: number;
}

export interface RepositoryZipClientSearchHit {
  name: string;
  path: string;
  type: 'file';
  extension: string | null;
  zip_path: string;
  zip_internal_path: string;
  internal_path: string;
}

type ZipListFn = (zipPath: string, internalPath?: string) => Observable<RepositoryListResponse>;

const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'tif', 'tiff', 'webp', 'bmp']);
const TEXT_EXTS = new Set(['txt', 'log', 'csv', 'md']);

export function isRepositoryZipArchiveListItem(item: {
  name?: string;
  path?: string | null;
  type?: string | null;
  extension?: string | null;
}): boolean {
  const type = (item.type ?? '').toLowerCase();
  if (type === 'folder' || type === 'directory') {
    return false;
  }
  const ext = (item.extension || '').toLowerCase();
  const path = `${item.path || ''} ${item.name || ''}`.toLowerCase();
  return ext === 'zip' || path.trim().endsWith('.zip');
}

export function isZipClientSearchNavigationEntry(name: string): boolean {
  const n = (name ?? '').trim();
  return n === '.' || n === '..' || n === '...';
}

export function isZipClientSearchTechnicalFile(name: string): boolean {
  const n = (name ?? '').trim().toLowerCase();
  if (!n) {
    return true;
  }
  if (n.endsWith('_meta.json') || n.endsWith('.meta.json')) {
    return true;
  }
  return n === 'meta.json' || n.endsWith('.checksum.json');
}

export function fileExtensionFromName(name: string): string {
  const base = (name ?? '').trim().split(/[/\\]/).pop() || '';
  const idx = base.lastIndexOf('.');
  if (idx <= 0 || idx === base.length - 1) {
    return '';
  }
  return base.slice(idx + 1).toLowerCase();
}

export function repositoryZipSearchFileMatches(
  fileName: string,
  criteria: RepositoryZipClientSearchCriteria,
): boolean {
  const name = (fileName ?? '').trim();
  if (!name || isZipClientSearchNavigationEntry(name) || isZipClientSearchTechnicalFile(name)) {
    return false;
  }
  const ext = fileExtensionFromName(name);
  const type = (criteria.fileType || 'all').toLowerCase();
  if (type === 'pdf' && ext !== 'pdf') {
    return false;
  }
  if (type === 'zip' && ext !== 'zip') {
    return false;
  }
  if (type === 'image' && !IMAGE_EXTS.has(ext)) {
    return false;
  }
  if (type === 'text' && !TEXT_EXTS.has(ext)) {
    return false;
  }
  const query = (criteria.query || '').trim();
  if (!query || query === '__ext_only__') {
    return type !== 'all';
  }
  if (query.length < 2) {
    return false;
  }
  return name.toLowerCase().includes(query.toLowerCase());
}

export function extractRepositoryListItems(raw: unknown): Array<Record<string, unknown>> {
  if (!raw || typeof raw !== 'object') {
    return [];
  }
  const record = raw as Record<string, unknown>;
  let list: unknown[] = [];
  if (Array.isArray(record['items'])) {
    list = record['items'] as unknown[];
  } else if (Array.isArray(record['results'])) {
    list = record['results'] as unknown[];
  } else if (Array.isArray(record['files'])) {
    list = record['files'] as unknown[];
  } else if (Array.isArray(raw)) {
    list = raw as unknown[];
  }
  return list.filter((it): it is Record<string, unknown> => !!it && typeof it === 'object');
}

export function resolveZipArchivePathFromListItem(
  item: { name?: string; path?: string | null },
  listingPath: string,
): string {
  const raw = (item.path || item.name || '').trim();
  return normalizeRepositoryExplorerPath(resolveRepositoryItemPath(raw, listingPath));
}

export function buildZipClientSearchHit(
  zipPath: string,
  internalPath: string,
  fileName: string,
): RepositoryZipClientSearchHit {
  const inner = normalizeZipInternalPath(internalPath);
  const name = (fileName || inner.split('/').pop() || '').trim();
  const zipNorm = normalizeRepositoryExplorerPath(zipPath);
  const ext = fileExtensionFromName(name) || null;
  return {
    name,
    path: inner ? `${zipNorm}::${inner}` : zipNorm,
    type: 'file',
    extension: ext,
    zip_path: zipNorm,
    zip_internal_path: inner,
    internal_path: inner,
  };
}

function listItemIsFolder(item: Record<string, unknown>): boolean {
  const type = String(item['type'] ?? (item['is_directory'] ? 'folder' : 'file')).toLowerCase();
  return type === 'folder' || type === 'directory';
}

function listItemName(item: Record<string, unknown>): string {
  return String(item['name'] ?? item['filename'] ?? '').trim();
}

/**
 * Recorre un ZIP con `listZip` (BFS por niveles) y devuelve hits que cumplen criterios.
 * Workaround cuando `GET /search?search_in_zips=true` responde vacío.
 */
export function searchInsideSingleZipArchive$(
  zipPath: string,
  criteria: RepositoryZipClientSearchCriteria,
  listZip: ZipListFn,
): Observable<RepositoryZipClientSearchHit[]> {
  const maxResults = Math.max(1, criteria.maxResults ?? 200);
  const maxDepth = Math.max(0, criteria.maxDepth ?? 12);
  const zipNorm = normalizeRepositoryExplorerPath(zipPath);
  if (!zipNorm) {
    return of([]);
  }

  const hits: RepositoryZipClientSearchHit[] = [];
  const seenHits = new Set<string>();
  const seenFolders = new Set<string>(['']);

  const scanLevel = (internalPath: string, depth: number): Observable<null> => {
    if (hits.length >= maxResults || depth > maxDepth) {
      return of(null);
    }
    return listZip(zipNorm, internalPath || undefined).pipe(
      catchError(() => of({ items: [] } as RepositoryListResponse)),
      concatMap((res) => {
        const folders: string[] = [];
        for (const item of extractRepositoryListItems(res)) {
          if (hits.length >= maxResults) {
            break;
          }
          const name = listItemName(item);
          if (!name || isZipClientSearchNavigationEntry(name)) {
            continue;
          }
          const segment = resolveZipInternalRowSegment({
            name,
            path: String(item['path'] ?? ''),
            internal_path:
              typeof item['internal_path'] === 'string' ? item['internal_path'] : null,
          });
          const fullInternal = internalPath
            ? resolveZipInternalNavigatePath(internalPath, segment)
            : normalizeZipInternalPath(
                extractZipInternalRelativePath(segment) || segment || name,
              );

          if (listItemIsFolder(item)) {
            const key = fullInternal.toLowerCase();
            if (!seenFolders.has(key) && depth < maxDepth) {
              seenFolders.add(key);
              folders.push(fullInternal);
            }
            continue;
          }

          if (!repositoryZipSearchFileMatches(name, criteria)) {
            continue;
          }
          const hitKey = `${zipNorm}::${fullInternal}`.toLowerCase();
          if (seenHits.has(hitKey)) {
            continue;
          }
          seenHits.add(hitKey);
          hits.push(buildZipClientSearchHit(zipNorm, fullInternal, name));
        }

        if (folders.length === 0 || hits.length >= maxResults) {
          return of(null);
        }
        return from(folders).pipe(
          concatMap((folder) => scanLevel(folder, depth + 1)),
          map(() => null),
        );
      }),
    );
  };

  return scanLevel('', 0).pipe(
    toArray(),
    map(() => hits.slice(0, maxResults)),
  );
}

/** Busca en varios ZIP en serie hasta `maxResults`. */
export function searchInsideZipArchives$(
  zipPaths: string[],
  criteria: RepositoryZipClientSearchCriteria,
  listZip: ZipListFn,
): Observable<RepositoryZipClientSearchHit[]> {
  const maxResults = Math.max(1, criteria.maxResults ?? 200);
  const maxZips = Math.max(1, criteria.maxZips ?? 20);
  const unique = [
    ...new Set(
      zipPaths.map((p) => normalizeRepositoryExplorerPath(p)).filter(Boolean),
    ),
  ].slice(0, maxZips);

  if (unique.length === 0) {
    return of([]);
  }

  const searchFrom = (
    index: number,
    acc: RepositoryZipClientSearchHit[],
  ): Observable<RepositoryZipClientSearchHit[]> => {
    if (acc.length >= maxResults || index >= unique.length) {
      return of(acc.slice(0, maxResults));
    }
    const remaining = maxResults - acc.length;
    return searchInsideSingleZipArchive$(
      unique[index],
      { ...criteria, maxResults: remaining },
      listZip,
    ).pipe(
      catchError(() => of([] as RepositoryZipClientSearchHit[])),
      switchMap((chunk) => searchFrom(index + 1, acc.concat(chunk))),
    );
  };

  return searchFrom(0, []);
}

/** Collect `.zip` paths from a folder listing (absolute). */
export function collectZipArchivePathsFromListing(
  listing: unknown,
  listingPath: string,
): string[] {
  const out: string[] = [];
  for (const item of extractRepositoryListItems(listing)) {
    const name = listItemName(item);
    const row = {
      name,
      path: String(item['path'] ?? name),
      type: String(item['type'] ?? ''),
      extension: typeof item['extension'] === 'string' ? item['extension'] : null,
    };
    if (!isRepositoryZipArchiveListItem(row)) {
      continue;
    }
    const zipPath = resolveZipArchivePathFromListItem(row, listingPath);
    if (zipPath) {
      out.push(zipPath);
    }
  }
  return out;
}

export function zipClientSearchHitsAsListItems(
  hits: RepositoryZipClientSearchHit[],
): RepositoryListItem[] {
  return hits.map((hit) => ({
    name: hit.name,
    path: hit.path,
    type: 'file' as const,
    extension: hit.extension,
    zip_path: hit.zip_path,
    zip_internal_path: hit.zip_internal_path,
    internal_path: hit.internal_path,
  }));
}

/** True when the API search payload has no usable rows. */
export function repositorySearchResponseIsEmpty(raw: unknown): boolean {
  return extractRepositoryListItems(raw).length === 0;
}
